import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, MenuItem, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { Add, Edit, ListAlt } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { EditableItemsTable } from '../../components/EditableItemsTable';
import { apiClient } from '../../api/client';
import { rateCardsApi, type RateCard } from '../../api/boqMasters.api';
import { clientsApi } from '../../api/clients.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';
import type { ApiSuccess } from '../../types';

type Row = Record<string, unknown>;
const ITEM_COLUMNS = [
  { key: 'description', label: 'Description' },
  { key: 'unit', label: 'Unit', width: 90 },
  { key: 'clientRate', label: 'Client Rate', type: 'number' as const, width: 140 },
];

export function RateCardListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [headerOpen, setHeaderOpen] = useState(false);
  const [editing, setEditing] = useState<RateCard | null>(null);
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState<number | ''>('');
  const [itemsFor, setItemsFor] = useState<RateCard | null>(null);
  const [items, setItems] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['rate-cards'], queryFn: () => rateCardsApi.list({ limit: 100 }) });
  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 100, status: 'active' }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['rate-cards'] });

  const saveHeader = useMutation({
    mutationFn: () => (editing ? rateCardsApi.update(editing.id, { name }) : rateCardsApi.create({ clientId: Number(clientId), name })),
    onSuccess: () => { setHeaderOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const saveItems = useMutation({
    mutationFn: () => rateCardsApi.setItems(itemsFor!.id, items),
    onSuccess: () => setItemsFor(null),
    onError: (e) => setError(extractApiError(e)),
  });

  const openItems = async (rc: RateCard) => {
    setError(null);
    const { data: detail } = await apiClient.get<ApiSuccess<RateCard & { items: Row[] }>>(`/rate-cards/${rc.id}`);
    setItems(detail.data.items ?? []);
    setItemsFor(rc);
  };

  const columns: Column<RateCard>[] = [
    { key: 'code', label: 'Code', width: 110 },
    { key: 'name', label: 'Name' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader title="Client Rate Card Mapping" action={hasPermission('rate_card.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setName(''); setClientId(''); setError(null); setHeaderOpen(true); }}>New Rate Card</Button>} />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={1} limit={data?.data.length || 25} total={data?.meta.total ?? 0}
        onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Edit items"><IconButton size="small" onClick={() => openItems(r)}><ListAlt fontSize="small" /></IconButton></Tooltip>
            {hasPermission('rate_card.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setName(r.name); setError(null); setHeaderOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <Dialog open={headerOpen} onClose={() => setHeaderOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? 'Edit Rate Card' : 'New Rate Card'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            {!editing && (
              <TextField select label="Client" value={clientId} onChange={(e) => setClientId(Number(e.target.value))} required fullWidth>
                {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setHeaderOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => saveHeader.mutate()} disabled={!name || (!editing && !clientId)}>Save</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(itemsFor)} onClose={() => setItemsFor(null)} fullWidth maxWidth="md">
        <DialogTitle>Rate Card Items — {itemsFor?.name}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <EditableItemsTable columns={ITEM_COLUMNS} rows={items} onChange={setItems} disabled={!hasPermission('rate_card.update')} newRow={() => ({ description: '', unit: '', clientRate: 0 })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemsFor(null)}>Close</Button>
          {hasPermission('rate_card.update') && <Button variant="contained" onClick={() => saveItems.mutate()}>Save Items</Button>}
        </DialogActions>
      </Dialog>
    </>
  );
}
